import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CircleCheck, OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function manageApiUsage(id: string, requiresApiUser: boolean) {
  return api.backend.put(
    `/application/manage-authentication/${id}?requiresApiUser=${requiresApiUser}`,
  )
}

export function useManageApiUsage() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    { id: string; requiresApiUser: boolean }
  >({
    mutationKey: ['manageApiUsage'],
    mutationFn: ({ id, requiresApiUser }) =>
      manageApiUsage(id, requiresApiUser),
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CircleCheck,
          title: t('applications.drawer_manage_api_usage.toast.success.title'),
          description: t(
            'applications.drawer_manage_api_usage.toast.success.message',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('applications.drawer_manage_api_usage.toast.error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('applications.drawer_manage_api_usage.toast.error.message'),
        },
      })
    },
  })

  const handleManageApiUsage = async (id: string, requiresApiUser: boolean) => {
    await mutateAsync({ id, requiresApiUser })
  }

  return {
    handleManageApiUsage,
    isManagingApiUsage: isPending,
  }
}
